/**
 * The grab handle: a finger-sized target beside a selected element while
 * Move is armed on a touch device.
 *
 * Two things a finger cannot do on the plan that a mouse can: hit a door
 * that is a few pixels tall at plan zoom, and see what it is holding —
 * the fingertip covers the element. So the drag target is a round handle
 * OFFSET from the element into the room, iOS-text-selection style, sized
 * for a finger, and the element itself is left uncovered. Pure geometry
 * here; the renderer draws it and the gesture hook hit-tests it.
 *
 * Everything is in WORLD metres; screen-sized constants are divided by the
 * canvas scale (px per metre) at the call site, so the handle keeps its
 * size on screen whatever the zoom.
 */
import { apply3DRotationTo2D } from './geometryUtils';

/** Visual radius on screen. */
export const GRAB_HANDLE_RADIUS_PX = 20;
/** Hit radius on screen — Apple's 44 pt minimum target, as a radius. */
export const GRAB_HANDLE_HIT_RADIUS_PX = 26;
/** Gap between the element's edge and the handle's near edge, on screen. */
export const GRAB_HANDLE_GAP_PX = 14;

/**
 * Centroid of a room's floor polygon — the "into the room" direction for
 * the handle. Null when the room has no polygon to speak of.
 *
 * @param {Object|null|undefined} room
 * @returns {{x: number, z: number} | null}
 */
export const roomCentroid = (room) => {
  const corners = room?.floors?.[0]?.polygonCorners;
  if (!Array.isArray(corners) || corners.length < 3) return null;
  let x = 0;
  let z = 0;
  for (const c of corners) {
    x += c.x || 0;
    z += c.z || 0;
  }
  return { x: x / corners.length, z: z / corners.length };
};

/**
 * Where the handle sits for `obj`: beside the element, across its
 * direction, on the side facing `roomCenter` (or the +normal side when
 * there is no room to face — screen-down on an axis-aligned wall).
 *
 * @param {Object} obj - element with center / dimensions / rotation
 * @param {number} scale - canvas px per metre
 * @param {{x: number, z: number} | null} [roomCenter]
 * @returns {{x: number, z: number} | null}
 */
export const grabHandlePosition = (obj, scale, roomCenter = null) => {
  if (!obj?.center || !obj?.dimensions || !(scale > 0)) return null;
  const dir = apply3DRotationTo2D(obj.rotation);
  // Perpendicular to the element's own direction, in the plan.
  let nx = -dir[1];
  let nz = dir[0];
  if (roomCenter) {
    const toRoom =
      (roomCenter.x - obj.center.x) * nx + (roomCenter.z - obj.center.z) * nz;
    if (toRoom < 0) {
      nx = -nx;
      nz = -nz;
    }
  }
  const halfDepth = (obj.dimensions.z || 0) / 2;
  const offset =
    halfDepth + (GRAB_HANDLE_GAP_PX + GRAB_HANDLE_RADIUS_PX) / scale;
  return { x: obj.center.x + nx * offset, z: obj.center.z + nz * offset };
};

/**
 * Does a plan point land on the handle?
 *
 * @param {{x: number, z: number}} point - world
 * @param {Object} obj
 * @param {number} scale
 * @param {{x: number, z: number} | null} [roomCenter]
 * @returns {boolean}
 */
export const hitTestGrabHandle = (point, obj, scale, roomCenter = null) => {
  const pos = grabHandlePosition(obj, scale, roomCenter);
  if (!pos || !point) return false;
  const r = GRAB_HANDLE_HIT_RADIUS_PX / scale;
  const dx = point.x - pos.x;
  const dz = point.z - pos.z;
  return dx * dx + dz * dz <= r * r;
};

/**
 * The finger handle itself: a filled disc with a white rim and a glyph.
 * `glyph` is `'move'` (four-way) or a unit direction `{x, z}` for a
 * two-way resize arrow along that axis. Shared by the move handle and
 * every resize / wall-end / run-end handle in coarse mode, so they all
 * read as the same kind of button.
 *
 * @param {CanvasRenderingContext2D} ctx - world space
 * @param {{x: number, z: number}} pos
 * @param {number} scale
 * @param {{glyph?: 'move' | {x: number, z: number}, active?: boolean, radiusPx?: number}} [opts]
 */
export const drawHandleDisc = (ctx, pos, scale, opts = {}) => {
  const {
    glyph = 'move',
    active = false,
    radiusPx = GRAB_HANDLE_RADIUS_PX,
    // 'finger': blue disc, white glyph (the touch button). 'mouse': white
    // disc, blue rim and glyph — the classic handle, now with the arrow
    // turned to the axis it resizes, which is what makes a slanted wall's
    // handles read correctly.
    palette = 'finger',
  } = opts;
  const mouse = palette === 'mouse';
  const r = radiusPx / scale;
  // 'muted': the handle is drawn but cannot move (a run end pinned by an
  // appliance) — grey, so it does not promise a drag.
  const ink = palette === 'muted' ? '#9ca3af' : active ? '#0047b3' : '#0066ff';
  ctx.save();
  ctx.fillStyle = mouse ? (active ? ink : '#ffffff') : ink;
  ctx.strokeStyle = mouse ? ink : '#ffffff';
  ctx.lineWidth = (mouse ? 1.5 : 2) / scale;
  ctx.beginPath();
  ctx.arc(pos.x, pos.z, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  const arm = r * 0.55;
  const head = r * 0.22;
  ctx.strokeStyle = mouse && !active ? ink : '#ffffff';
  ctx.lineWidth = (mouse ? 1.5 : 2) / scale;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  const axes =
    glyph === 'move'
      ? [
          [1, 0],
          [-1, 0],
          [0, 1],
          [0, -1],
        ]
      : [
          [glyph.x, glyph.z],
          [-glyph.x, -glyph.z],
        ];
  for (const [ax, az] of axes) {
    const tx = pos.x + ax * arm;
    const tz = pos.z + az * arm;
    ctx.moveTo(pos.x, pos.z);
    ctx.lineTo(tx, tz);
    // Two short strokes back from the tip, either side of the axis.
    ctx.moveTo(tx - ax * head - az * head, tz - az * head + ax * head);
    ctx.lineTo(tx, tz);
    ctx.lineTo(tx - ax * head + az * head, tz - az * head - ax * head);
  }
  ctx.stroke();
  ctx.restore();
};

/**
 * Draw the move handle: the disc plus a thin tether back to the element
 * so the pairing is unmistakable.
 *
 * @param {CanvasRenderingContext2D} ctx - already in world space
 * @param {Object} obj
 * @param {number} scale
 * @param {{x: number, z: number} | null} [roomCenter]
 * @param {{glyph?: 'move' | {x: number, z: number}}} [opts] — a two-way
 *   arrow along that axis for something that can only slide along it (a bay
 *   on its run); the four-way `'move'` otherwise
 */
export const drawGrabHandle = (
  ctx,
  obj,
  scale,
  roomCenter = null,
  { glyph = 'move' } = {}
) => {
  const pos = grabHandlePosition(obj, scale, roomCenter);
  if (!pos) return;
  ctx.save();
  ctx.strokeStyle = 'rgba(0, 102, 255, 0.55)';
  ctx.lineWidth = 1.5 / scale;
  ctx.setLineDash([4 / scale, 3 / scale]);
  ctx.beginPath();
  ctx.moveTo(obj.center.x, obj.center.z);
  ctx.lineTo(pos.x, pos.z);
  ctx.stroke();
  ctx.restore();
  drawHandleDisc(ctx, pos, scale, { glyph });
};

/**
 * Where an opening sits along its wall, from each end, for the live
 * readout during a finger drag (the finger covers the door; the numbers
 * say where it is). Distances are edge-to-end: how much wall is left on
 * either side of the opening.
 *
 * @param {Object} opening - center / dimensions
 * @param {Object} wall - center / rotation / dimensions
 * @returns {{toStart: number, toEnd: number} | null} metres
 */
export const wallSpanFromEnds = (opening, wall) => {
  if (!opening?.center || !wall?.center || !wall?.dimensions?.x) return null;
  const dir = apply3DRotationTo2D(wall.rotation);
  const half = wall.dimensions.x / 2;
  const startX = wall.center.x - dir[0] * half;
  const startZ = wall.center.z - dir[1] * half;
  const along =
    (opening.center.x - startX) * dir[0] + (opening.center.z - startZ) * dir[1];
  const halfWidth = (opening.dimensions?.x || 0) / 2;
  return {
    toStart: along - halfWidth,
    toEnd: wall.dimensions.x - along - halfWidth,
  };
};

/**
 * A text pill above a plan point (screen-up = −Z; the plan canvas never
 * rotates). The finger covers what it drags, so the numbers go here.
 *
 * @param {CanvasRenderingContext2D} ctx - world space
 * @param {{x: number, z: number}} anchor
 * @param {string} text
 * @param {number} scale
 */
export const drawReadoutPill = (ctx, anchor, text, scale) => {
  if (!anchor || !text) return;
  const fontPx = 13;
  const padX = 10 / scale;
  const padY = 6 / scale;
  const lift = 64 / scale;
  ctx.save();
  ctx.font = `600 ${fontPx / scale}px system-ui, -apple-system, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const w = ctx.measureText(text).width + padX * 2;
  const h = fontPx / scale + padY * 2;
  const cx = anchor.x;
  const cy = anchor.z - lift;
  ctx.fillStyle = 'rgba(17, 24, 39, 0.88)';
  ctx.beginPath();
  const r = h / 2;
  ctx.moveTo(cx - w / 2 + r, cy - h / 2);
  ctx.arcTo(cx + w / 2, cy - h / 2, cx + w / 2, cy + h / 2, r);
  ctx.arcTo(cx + w / 2, cy + h / 2, cx - w / 2, cy + h / 2, r);
  ctx.arcTo(cx - w / 2, cy + h / 2, cx - w / 2, cy - h / 2, r);
  ctx.arcTo(cx - w / 2, cy - h / 2, cx + w / 2, cy - h / 2, r);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.fillText(text, cx, cy);
  ctx.restore();
};

/**
 * The wall-span readout for a finger-dragged opening.
 *
 * @param {CanvasRenderingContext2D} ctx - world space
 * @param {Object} opening
 * @param {{toStart: number, toEnd: number}} span
 * @param {number} scale
 * @param {(m: number) => string} format
 */
export const drawWallSpanReadout = (ctx, opening, span, scale, format) => {
  if (!opening?.center || !span) return;
  drawReadoutPill(
    ctx,
    opening.center,
    `${format(span.toStart)}  ↔  ${format(span.toEnd)}`,
    scale
  );
};
